/**
 * chatApi — data layer for the messaging engine (cert chat + event chats).
 * Spec: docs/messaging-engine-spec.md. Tables: chats / chat_members /
 * messages / message_reactions (migration 2026-06-03_messaging_engine.sql).
 *
 * Phase 1: clients read via RLS and send messages with a direct insert so we
 * can dogfood on web. Phase 2 routes sends through a `post-message` edge
 * function for server-side push fanout (this file's sendMessage swaps over
 * then — callers don't change).
 */

import { avatarSrc } from "../utils/avatar";
import { getBlockedIds } from "../community/blocklist";
import { supabase } from "../../supabaseClient";

// Fixed id of the seeded singleton cert chat (see migration).
export const CERT_CHAT_ID = "ce27c0a7-0000-4000-8000-000000000001";

/**
 * My user id, resolved once.
 *
 * `supabase.auth.getUser()` goes to the network. It was awaited before
 * anything else on every chat open, so every thread paid a round trip to
 * learn something that cannot change while the tab is open. Cached as a
 * promise rather than a value so simultaneous callers share one request
 * instead of racing three.
 */
let _mePromise = null;
export async function getMyUserId() {
  if (!_mePromise) {
    _mePromise = supabase.auth.getUser()
      .then(({ data }) => data?.user?.id || null)
      .catch(() => { _mePromise = null; return null; });
  }
  return _mePromise;
}

/** Signing out must drop it, or the next account inherits this one's id. */
export function resetChatIdentity() { _mePromise = null; clearProfileCache(); }

/** Does this user belong to ≥1 chat? Drives nav visibility (cheap head count). */
export async function hasAnyChat(userId) {
  if (!userId) return false;
  const { count } = await supabase
    .from("chat_members")
    .select("*", { count: "exact", head: true })
    .eq("user_id", userId);
  return (count || 0) > 0;
}

/** Find or open the direct message with another coach; returns its chat id.
 *  The start_dm RPC refuses if either coach has blocked the other. */
export async function startDm(otherUserId) {
  const { data, error } = await supabase.rpc("start_dm", { p_other: otherUserId });
  if (error) throw new Error(/blocked/i.test(error.message || "") ? "You can't message this coach." : (error.message || "Couldn't start the chat."));
  return data;
}

/** Chats I'm in, enriched with last message + unread count + mute state. */
export async function listMyChats(userId) {
  const { data: mems, error: mErr } = await supabase
    .from("chat_members")
    .select("chat_id, last_read_at, muted, role")
    .eq("user_id", userId);
  if (mErr) throw mErr;
  if (!mems?.length) return [];

  const memByChat = Object.fromEntries(mems.map((m) => [m.chat_id, m]));
  const chatIds = mems.map((m) => m.chat_id);

  const { data: chats, error: cErr } = await supabase
    .from("chats")
    .select("id, type, title, created_at, archived_at")
    .in("id", chatIds)
    .is("archived_at", null);
  if (cErr) throw cErr;

  const enriched = await Promise.all(
    (chats || []).map(async (c) => {
      const mem = memByChat[c.id];
      const [{ data: lastArr }, { count }, { count: memberCount }, { data: sampleRows }] = await Promise.all([
        supabase
          .from("messages")
          .select("id, body, media_type, sender_id, created_at")
          .eq("chat_id", c.id)
          .is("deleted_at", null)
          .order("created_at", { ascending: false })
          .limit(1),
        supabase
          .from("messages")
          .select("*", { count: "exact", head: true })
          .eq("chat_id", c.id)
          .is("deleted_at", null)
          .gt("created_at", mem.last_read_at)
          .neq("sender_id", userId),
        // Two cheap queries rather than one fat one. This used to select
        // every member row of every chat, for a number and six avatars:
        // the founding chat alone has 541 members, so the list page was
        // transferring thousands of rows to render a subtitle. The count
        // now comes back as a header with no rows at all, and the sample
        // asks for the six it actually draws.
        supabase.from("chat_members").select("*", { count: "exact", head: true }).eq("chat_id", c.id),
        supabase.from("chat_members").select("user_id").eq("chat_id", c.id).limit(6),
      ]);
      return {
        ...c,
        muted: mem.muted,
        myRole: mem.role,
        lastMessage: lastArr?.[0] || null,
        unread: count || 0,
        memberCount: memberCount || 0,
        sampleIds: (sampleRows || []).map((r) => r.user_id),
      };
    })
  );

  // Batch-fetch a few member avatars per chat for the list cards.
  const allSample = [...new Set(enriched.flatMap((c) => c.sampleIds))];
  const sampleProfiles = await getProfiles(allSample);
  enriched.forEach((c) => { c.sampleMembers = c.sampleIds.map((id) => sampleProfiles[id]).filter(Boolean); });

  // A direct message has no title of its own: it's named for the other
  // coach. One with someone I've blocked doesn't show at all.
  const blocked = new Set(getBlockedIds());
  enriched.forEach((c) => {
    if (c.type !== "dm") return;
    c.dmUserId = c.sampleIds.find((id) => id !== userId) || null;
    c.title = c.dmUserId ? displayName(sampleProfiles[c.dmUserId]) : "Direct message";
    c.sampleMembers = c.sampleMembers.filter((p) => p.id !== userId);
  });
  for (let i = enriched.length - 1; i >= 0; i--) {
    if (enriched[i].type === "dm" && blocked.has(enriched[i].dmUserId)) enriched.splice(i, 1);
  }

  // Cert chat pinned on top; otherwise most-recent-activity first.
  enriched.sort((a, b) => {
    if (a.type === "cert" && b.type !== "cert") return -1;
    if (b.type === "cert" && a.type !== "cert") return 1;
    const at = a.lastMessage?.created_at || a.created_at;
    const bt = b.lastMessage?.created_at || b.created_at;
    return new Date(bt) - new Date(at);
  });
  return enriched;
}

const MSG_COLS = "id, chat_id, sender_id, body, media_type, media_path, media_duration_ms, mux_playback_id, reply_to, edited_at, pinned_at, pinned_by, created_at";

/** A page of messages, returned chronologically (oldest→newest) for display. */
export async function listMessages(chatId, { limit = 50, before = null } = {}) {
  let q = supabase
    .from("messages")
    .select(MSG_COLS)
    .eq("chat_id", chatId)
    .is("deleted_at", null)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (before) q = q.lt("created_at", before);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).reverse();
}

export async function sendMessage(chatId, { body = null, mediaType = null, mediaPath = null, mediaDurationMs = null, muxPlaybackId = null, replyTo = null } = {}) {
  // Prefer the post-message edge function: it inserts AND fans out push
  // server-side. Fall back to a direct insert if it isn't deployed yet, so the
  // chat keeps working either way (push just won't fire until it's deployed).
  try {
    const { data, error } = await supabase.functions.invoke("post-message", {
      body: { chat_id: chatId, body, media_type: mediaType, media_path: mediaPath, media_duration_ms: mediaDurationMs, mux_playback_id: muxPlaybackId, reply_to: replyTo },
    });
    if (!error && data?.message) return data.message;
  } catch { /* fall through to direct insert */ }

  const userId = await getMyUserId();
  const { data, error } = await supabase
    .from("messages")
    .insert({ chat_id: chatId, sender_id: userId, body, media_type: mediaType, media_path: mediaPath, media_duration_ms: mediaDurationMs, mux_playback_id: muxPlaybackId, reply_to: replyTo })
    .select(MSG_COLS)
    .single();
  if (error) throw error;
  return data;
}

export async function editMessage(messageId, body) {
  const { data, error } = await supabase
    .from("messages")
    .update({ body, edited_at: new Date().toISOString() })
    .eq("id", messageId)
    .select(MSG_COLS)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteMessage(messageId) {
  const { error } = await supabase
    .from("messages")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", messageId);
  if (error) throw error;
}

/** Soft-delete every message in a chat. Chat admins only (enforced in the
 *  RPC). Returns how many were cleared so the caller can say so. */
export async function clearChat(chatId) {
  const { data, error } = await supabase.rpc("clear_chat", { p_chat: chatId });
  if (error) throw error;
  return data || 0;
}

/** Soft-delete somebody else's message. Chat admins only. RLS scopes plain
 *  updates to your own messages, so this has to go through an RPC. */
export async function adminDeleteMessage(messageId) {
  const { error } = await supabase.rpc("admin_delete_message", { p_message: messageId });
  if (error) throw error;
}

export async function setPinned(messageId, pinned) {
  await supabase.rpc("set_message_pinned", { p_message: messageId, p_pinned: pinned });
}

/** Currently-pinned messages in a chat (newest pin first). */
export async function listPinned(chatId) {
  const { data, error } = await supabase
    .from("messages")
    .select(MSG_COLS)
    .eq("chat_id", chatId)
    .not("pinned_at", "is", null)
    .is("deleted_at", null)
    .order("pinned_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

/** Per-member read state ({ user_id, last_read_at }) — powers "seen by" + ✓✓. */
export async function listReadReceipts(chatId) {
  const { data, error } = await supabase
    .from("chat_members")
    .select("user_id, last_read_at")
    .eq("chat_id", chatId);
  if (error) throw error;
  return data || [];
}

/** Upload an image to the chat-media bucket; returns its public URL + path. */
export async function uploadChatImage(chatId, file) {
  const userId = await getMyUserId();
  const ext = (file.name?.split(".").pop() || "jpg").toLowerCase();
  const rand = Math.random().toString(36).slice(2, 10);
  const path = `${chatId}/${userId}-${rand}.${ext}`;
  const { error } = await supabase.storage.from("chat-media").upload(path, file, { contentType: file.type, upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from("chat-media").getPublicUrl(path);
  return { path, url: data.publicUrl };
}

/** Upload a recorded voice note (Blob) to chat-media; returns its URL + path. */
export async function uploadChatAudio(chatId, blob, ext = "webm") {
  const userId = await getMyUserId();
  const rand = Math.random().toString(36).slice(2, 10);
  const path = `${chatId}/${userId}-${rand}.${ext}`;
  const { error } = await supabase.storage.from("chat-media").upload(path, blob, { contentType: blob.type || `audio/${ext}`, upsert: false });
  if (error) throw error;
  const { data } = supabase.storage.from("chat-media").getPublicUrl(path);
  return { path, url: data.publicUrl };
}

/* Mark a chat read. Throws on failure instead of swallowing it: a read that
   silently never saved is how a chat stays "unread" after being opened again
   and again. `false` (from the 2026-10-18 version of the RPC) means the
   update matched no membership row. */
export async function markRead(chatId) {
  const { data, error } = await supabase.rpc("mark_chat_read", { p_chat: chatId });
  if (error) throw error;
  if (data === false) console.warn("[chat] markRead matched no membership row", chatId);
}

export async function setMuted(chatId, muted) {
  await supabase.rpc("set_chat_muted", { p_chat: chatId, p_muted: muted });
}

export async function setMentionOnly(chatId, value) {
  await supabase.rpc("set_chat_mention_only", { p_chat: chatId, p_value: value });
}

/** How many people are in a chat. A header count, so no rows cross the
 *  wire: the thread title needs the number long before it needs the list. */
export async function countMembers(chatId) {
  const { count } = await supabase
    .from("chat_members")
    .select("*", { count: "exact", head: true })
    .eq("chat_id", chatId);
  return count || 0;
}

export async function listMembers(chatId) {
  const { data, error } = await supabase
    .from("chat_members")
    .select("user_id, role, joined_at")
    .eq("chat_id", chatId);
  if (error) throw error;
  return data || [];
}

/** Search members to add to a chat (by name or handle). */
export async function searchProfiles(query, { limit = 20 } = {}) {
  const q = (query || "").replace(/[%,()]/g, " ").trim();
  if (q.length < 2) return [];
  const { data, error } = await supabase
    .from("profiles")
    .select("id, first_name, last_name, handle, avatar_url")
    .or(`first_name.ilike.%${q}%,last_name.ilike.%${q}%,handle.ilike.%${q}%`)
    .limit(limit);
  if (error) throw error;
  return data || [];
}

/** Admin: create an event/group chat, name it, seed members. Returns chat id. */
export async function createEventChat(title, memberIds) {
  const { data, error } = await supabase.rpc("create_event_chat", { p_title: title, p_member_ids: memberIds || [] });
  if (error) throw error;
  return data;
}

/** Is the current user a platform admin? (gates the New-chat button.) */
export async function amIAdmin() {
  const uid = await getMyUserId();
  if (!uid) return false;
  const { data } = await supabase.from("profiles").select("is_admin").eq("id", uid).limit(1);
  return !!data?.[0]?.is_admin;
}

/** Map of userId → minimal profile (name/handle/avatar) for sender display. */
/**
 * Display names and avatars, cached for the session.
 *
 * These were refetched in full every time a chat opened, and the fetch sat
 * at the END of a four-step chain, so every message rendered as "Coach"
 * until the last round trip landed. People do not rename themselves mid
 * session, so the second open of any chat should not ask again.
 *
 * Only unknown ids go to the network. An id that comes back empty is still
 * recorded, as null, so a deleted account is not re-requested on every
 * render of every message they ever sent.
 */
const _profileCache = new Map();
export function clearProfileCache() { _profileCache.clear(); }
/** Whatever is already known, with no network call. Lets the thread paint
 *  real names on the first frame when it has seen these people before. */
export function cachedProfiles(userIds) {
  const out = {};
  for (const id of userIds || []) {
    const hit = _profileCache.get(id);
    if (hit) out[id] = hit;
  }
  return out;
}

export async function getProfiles(userIds) {
  const ids = [...new Set(userIds.filter(Boolean))];
  if (!ids.length) return {};
  const known = {};
  const missing = [];
  for (const id of ids) {
    if (_profileCache.has(id)) {
      const hit = _profileCache.get(id);
      if (hit) known[id] = hit;
    } else missing.push(id);
  }
  if (!missing.length) return known;
  const fetched = await fetchProfiles(missing);
  for (const id of missing) _profileCache.set(id, fetched[id] || null);
  return { ...known, ...fetched };
}

async function fetchProfiles(ids) {
  // profiles RLS only lets a coach read their own row, so a direct select
  // returned nothing for everyone else and the chat showed "Coach" for every
  // name. public_profiles is a definer RPC that returns display fields only.
  // Fall back to the direct select if the RPC hasn't been created yet.
  // Avatars at the size chat draws them (utils/avatar.js), never the
  // original upload, which for some coaches is several megabytes.
  const sized = (p) => ({ ...p, avatar_url: avatarSrc(p.avatar_url, 40) });
  const rpc = await supabase.rpc("public_profiles", { p_ids: ids });
  if (!rpc.error) return Object.fromEntries((rpc.data || []).map((p) => [p.id, sized(p)]));
  const { data, error } = await supabase
    .from("profiles")
    .select("id, first_name, last_name, handle, avatar_url")
    .in("id", ids);
  if (error) throw error;
  return Object.fromEntries((data || []).map((p) => [p.id, sized(p)]));
}

/** Reactions grouped by message_id: { [messageId]: [{ emoji, user_id }] }. */
export async function listReactions(messageIds) {
  const ids = [...new Set(messageIds.filter(Boolean))];
  if (!ids.length) return {};
  const { data, error } = await supabase
    .from("message_reactions")
    .select("message_id, user_id, emoji")
    .in("message_id", ids);
  if (error) throw error;
  const out = {};
  for (const r of data || []) (out[r.message_id] ||= []).push(r);
  return out;
}

export async function toggleReaction(messageId, emoji, userId) {
  const { data: existing } = await supabase
    .from("message_reactions")
    .select("emoji")
    .eq("message_id", messageId)
    .eq("user_id", userId)
    .eq("emoji", emoji)
    .maybeSingle();
  if (existing) {
    await supabase.from("message_reactions").delete().eq("message_id", messageId).eq("user_id", userId).eq("emoji", emoji);
    return false;
  }
  await supabase.from("message_reactions").insert({ message_id: messageId, user_id: userId, emoji });
  return true;
}

export function displayName(profile) {
  if (!profile) return "Coach";
  const full = [profile.first_name, profile.last_name].filter(Boolean).join(" ").trim();
  if (full) return full;
  if (profile.handle) return `@${profile.handle}`;
  // Derive a stable per-coach identifier from the profile id so multiple
  // un-onboarded coaches in a chat thread don't all show as the same
  // "Coach" — matches the community feed's fallback shape.
  if (profile.id) {
    const hex = String(profile.id).replace(/-/g, "").slice(-4).toUpperCase();
    return `Coach ${hex}`;
  }
  return "Coach";
}

export function initials(profile) {
  const n = displayName(profile);
  return n.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "C";
}
